import { BaseListViewStore, BooleanCellData, HyperlinkCellData, ShippingCostCellData } from 'core/stores';
import { observable, action, makeObservable } from 'mobx';
import { localStorageHandler } from 'core/services';
import { ShipmentInfoStore } from 'modules/shipment-info/stores';
import { BaasicTableSettingsViewStore } from 'common/stores';
import {
	TableFilterUtility,
	checkForColumnFilter,
	checkForColumnWidth,
	RemoveFiltersModalUtility,
	isWarehouse,
	isSuperAdminOrganization,
} from 'core/utils';
import { TableFilterTemplate, TableFilterNumericTemplate, TableFilterDateTemplate } from 'themes/components';
import { LookupStore } from 'modules/lookup/stores';
import { channelType, lookupRouteName } from 'common/constants';
import { DateTimeCellData } from 'core/stores';
import {
	FulfillmentOrderService,
	CarrierServiceService,
	ChannelService,
	ApplicationService,
	OrderService,
} from 'common/data';
import {
	OrderChannelHyperLinkCellData,
	HyperLinkTrackingNumberCellData,
	TooltipDateTimeCellData,
} from 'core/stores/CellDataStore';
import _ from 'lodash';

class ShipmentInfoListViewStore extends BaseListViewStore {
	@observable change = false;
	@observable showYesterdayShipments;
	showActions = false;
	localStorageWidthKey = 'shipment-infos-column-width';

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ShipmentInfoStore(rootStore),
			queryParams: {
				embed: [
					'shipmentInfoStatus',
					'warehouse',
					'fulfillmentOrder',
					// required for the backend inventory deduction during the find call in case the shipment has been picked up
					'fulfillmentOrder.fulfillmentOrderItems',
					'fulfillmentOrder.carrierService',
					'fulfillmentOrder.carrierService.carrierType',
					'fulfillmentOrder.fulfillmentOrderStatus',
					'fulfillmentOrder.order',
					'fulfillmentOrder.order.channel',
					'fulfillmentOrder.order.channel.channelType',
					'seller',
					'carrier',
					'carrier.carrierType',
					'carrierDeliveryStatus',
					'carrierDeliveryStatus.shipmentInfoDeliveryStatus',
				],
				filter: localStorageHandler.get('shipment-infos-filter'),
			},
			tableData: {
				columns: [
					{
						key: 'trackingNumber',
						title: 'SHIPMENTSTATUSVIEW.TRACKINGNUMBER',
						dataCell: HyperLinkTrackingNumberCellData,
						carrierServicePath: 'fulfillmentOrder.carrierService.name',
						selected: true,
						shippedOnly: true,
						service: 'shipmentInfoService',
						width: 200,
						filter: null,
						locked: true,
						sortable: true,
						filterType: TableFilterTemplate,
						dontSplit: true,
						service: 'shipmentInfoService',
					},
					{
						key: 'seller.applicationTitle',
						title: 'SHIPMENTSTATUSVIEW.SELLER',
						service: 'applicationService',
						sellerOnly: true,
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						filterKey: 'applicationTitle',
						enabled: isWarehouse(rootStore) || isSuperAdminOrganization(rootStore),
						dontSplit: true,
					},
					{
						key: 'warehouse.applicationTitle',
						title: 'SHIPMENTSTATUSVIEW.SHIPFROMWAREHOUSE',
						service: 'applicationService',
						isWarehouseOnlyColumn: true,
						isClickable: false,
						sortable: true,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						dontSplit: true,
						embed: ['warehouse'],
					},
					{
						key: 'fulfillmentOrder.order.channel.name',
						title: 'SHIPMENTSTATUSVIEW.CHANNEL',
						service: 'channelService',
						selected: false,
						checkForWarehouseLogin: true,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						isClickable: true,
						sortable: true,
						canFilter: true,
						dontSplit: true,
						embed: ['fulfillmentOrder', 'fulfillmentOrder.order', 'fulfillmentOrder.order.channel'],
						searchValues: 'name',
					},
					{
						key: 'fulfillmentOrder.order.channel.channelType.name',
						title: 'SHIPMENTSTATUSVIEW.CHANNELTYPE',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						isClickable: true,
						sortable: true,
						lookupConstant: lookupRouteName.channelType,
					},
					{
						key: 'fulfillmentOrder.order.orderNumber',
						title: 'SHIPMENTSTATUSVIEW.ORDERNUMBER',
						service: 'orderService',
						checkPartiallyFulfillMixedOrders: true,
						selected: false,
						dataCell: OrderChannelHyperLinkCellData,
						onClick: (order, channelTypeName) => this.openChannelWebsite(order, channelTypeName),
						checkForWarehouseLogin: true,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						isClickable: true,
						sortable: true,
						canFilter: true,
						dontSplit: true,
						embed: ['fulfillmentOrder', 'fulfillmentOrder.order'],
						searchValues: 'orderNumber',
					},
					{
						key: 'fulfillmentOrder.fulfillmentOrderId',
						title: 'SHIPMENTSTATUSVIEW.FULFILLMENTORDERID',
						service: 'fulfillmentOrderService',
						dataCell: HyperlinkCellData,
						onClick: dataItem => this.openOrderPage(dataItem.id),
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						filterType: TableFilterNumericTemplate,
						dontSplit: true,
					},
					{
						key: 'estimatedArrivalDate',
						title: 'SHIPMENTSTATUSVIEW.ESTIMATEDDELIVERYDATE',
						dateFormat: 'MM/DD/YYYY',
						dataCell: DateTimeCellData,
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						filterType: TableFilterDateTemplate,
						dontSplit: true,
					},
					{
						key: 'shippingDate',
						title: 'SHIPMENTSTATUSVIEW.SHIPPINGDATE',
						dateFormat: 'LLL',
						dataCell: DateTimeCellData,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						sortable: true,
						dontSplit: true,
						useLocalTime: true,
					},
					{
						key: 'cost',
						title: 'SHIPMENTSTATUSVIEW.COST',
						dataCell: ShippingCostCellData,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterNumericTemplate,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'isBuyShipping',
						title: 'SHIPMENTSTATUSVIEW.ISBUYSHIPPING',
						dataCell: BooleanCellData,
						selected: false,
						sortable: true,
						width: 120,
						filter: null,
						filterType: TableFilterTemplate,
						dontSplit: true,
					},
					{
						key: 'fulfillmentOrder.carrierService.name',
						title: 'SHIPMENTSTATUSVIEW.CARRIERSERVICE',
						service: 'carrierServiceService',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'fulfillmentOrder.fulfillmentOrderStatus.name',
						title: 'SHIPMENTSTATUSVIEW.FULFILLMENTORDERSTATUS',
						lookupConstant: lookupRouteName.fulfillmentOrderStatus,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
					},
					{
						key: 'carrierDeliveryStatus.name',
						title: 'SHIPMENTSTATUSVIEW.CARRIERDELIVERYSTATUS',
						lookupConstant: lookupRouteName.carrierDeliveryStatus,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
						embed: ['carrierDeliveryStatus'],
					},
					{
						key: 'carrierDeliveryStatus.shipmentInfoDeliveryStatus.name',
						title: 'SHIPMENTSTATUSVIEW.SHIPMENTINFODELIVERYSTATUS',
						lookupConstant: lookupRouteName.shipmentInfoDeliveryStatus,
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						filterType: TableFilterTemplate,
						dontSplit: true,
						embed: ['carrierDeliveryStatus', 'carrierDeliveryStatus.shipmentInfoDeliveryStatus'],
					},
					{
						key: 'zone',
						title: 'SHIPMENTSTATUSVIEW.ZONE',
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						filterType: TableFilterNumericTemplate,
					},
					{
						key: 'fulfillmentOrder.deliverByDate',
						title: 'SHIPMENTSTATUSVIEW.DELIVERBYDATE',
						dateFormat: 'L',
						dataCell: TooltipDateTimeCellData,
						newPropertyToUse: 'fulfillmentOrder.newDeliverByDate',
						translationKey: 'SHIPMENTSTATUSVIEW.NEWDELIVERBYDATE',
						service: 'fulfillmentOrderService',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterDateTemplate,
						sortable: true,
						useLocalTime: false,
					},
				],
				sortObject: [
					{
						field: 'trackingNumber',
						dir: 'asc',
					},
				],
				actions: {},
			},
			permissions: {
				update: () => this.authStore.hasPermission('fulfillmentSection.update'),
				create: () => this.authStore.hasPermission('fulfillmentSection.create'),
				remove: () => this.authStore.hasPermission('fulfillmentSection.remove'),
				read: () => this.authStore.hasPermission('fulfillmentSection.read'),
				full: () => this.authStore.hasPermission('fulfillmentSection.full'),
			},
			routes: {},
		});
		makeObservable(this);
		if (isSuperAdminOrganization(rootStore) || isWarehouse(rootStore)) {
			this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		this.queryUtility.initialize(this.findShipmentInfos);
		checkForColumnFilter(localStorageHandler.get('shipment-infos-table-data'), this.tableData.columns);
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.removeFiltersModal = new RemoveFiltersModalUtility({ viewStore: this });
		this.tableFilter = new TableFilterUtility({
			lookupStore: new LookupStore(rootStore),
			listStore: this.listStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
			services: [
				this.listStore.service,
				new FulfillmentOrderService(rootStore.app.baasic.apiClient),
				new CarrierServiceService(rootStore.app.baasic.apiClient),
				new ChannelService(rootStore.app.baasic.apiClient),
				new ApplicationService(rootStore.app.baasic.apiClient),
				new OrderService(rootStore.app.baasic.apiClient),
			],
		});
		this.setShowYesterdayShipments();
		this.setSearchVisible();
		this.setButtons();
	}

	@action.bound
	setShowYesterdayShipments() {
		var showYesterdayShipments = localStorageHandler.get('show-yesterday-shipments');
		if (showYesterdayShipments != null) {
			this.showYesterdayShipments = showYesterdayShipments;
		} else {
			this.showYesterdayShipments = true;
		}
	}
	openOrderPage = id => {
		this.rootStore.routerStore.goTo('master.app.main.order.list.detail', {
			id: id,
		});
	};
	openChannelWebsite = (order, channelTypeName) => {
		let url = null;
		switch (channelTypeName) {
			case channelType.amazon:
				url = `https://sellercentral.amazon.com/orders-v3/order/${order.orderNumber}`;
				break;
			case channelType.wooCommerce:
				url = `https://milliardbedding.com/wp-admin/post.php?post=${order.orderNumber}&action=edit`;
				break;
			case channelType.wayfair:
				url = `https://partners.wayfair.com/d/outbound-orders/po/${order.orderNumber}`;
				break;
			default:
				url = `https://portal.logicbroker.com/order-management/order-details?orderid=${order.logicBrokerKey}`;
				break;
		}
		if (url) window.open(url, '_blank');
	};
	@action.bound
	setButtons() {
		this.setButtonComponent({
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-cog spc--right--sml',
					onClick: this.baasicTableSettingsViewStore.openTableSettingsModal,
					text: 'SETTINGS',
				},
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-trash spc--right--sml',
					onClick: () => {
						this.removeFiltersModal.handleRemoveFiltersButtonClick();
					},
					text: 'FILTER.REMOVEALL',
					disabled: !!(this.queryUtility.filter.advancedSearch === undefined),
				},
			],
		});
	}

	@action.bound async findShipmentInfos() {
		this.suspend();
		if (this.baasicTableSettingsViewStore == undefined) {
			this.baasicTableSettingsViewStore = new BaasicTableSettingsViewStore(this.rootStore);
		}
		await this.baasicTableSettingsViewStore.setTemplates(
			this.tableData,
			'ShipmentInfosTableSettings',
			this.setAsChanged,
			this.findShipmentInfos
		);
		this.queryUtility.setFilterParams(
			this,
			{
				ignoreEmptyTracking: true,
				shippedOnly: true,
				excludeShipmentsWithNoCarrier: true,
				showYesterdayShipments: this.showYesterdayShipments,
			},
			this.tableFilter
		);
		await this.listStore.find(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.queryUtility.setFilterPersistance(this, 'shipment-infos-filter', 'shipment-infos-table-data');
		this.setButtons();
		this.resume();
	}

	@action.bound
	setAsChanged() {
		this.change = !this.change;
	}

	@action.bound onCheck = async check => {
		if (check) {
			this.showYesterdayShipments = check.currentTarget.checked;
			localStorageHandler.set('show-yesterday-shipments', this.showYesterdayShipments);
			this.findShipmentInfos();
		}
	};
}

export default ShipmentInfoListViewStore;



// WEBPACK FOOTER //
// ./src/modules/shipment-info/stores/ShipmentInfoListViewStore.js